import {
  COLS,
  ROWS,
  opponentPlayer,
  dropDisc,
  findWinningCells,
  isBoardFull,
  isColumnFull,
} from "./connectFour";

/*
 * Difficulty tuning — kept deliberately beatable for casual players.
 * MAX_DEPTH: how many moves ahead the CPU looks (was 8 = near-unbeatable).
 * BLOCK_CHANCE: odds the CPU blocks your immediate winning move.
 * MISTAKE_CHANCE: odds the CPU plays a random move instead of its best one.
 * NEAR_BEST_MARGIN: moves scoring within this of the best are picked at random,
 *   so the same moves don't always get the same reply.
 */
const MAX_DEPTH = 2;
const BLOCK_CHANCE = 0.6;
const MISTAKE_CHANCE = 0.35;
const NEAR_BEST_MARGIN = 15;
const WIN_SCORE = 1_000_000;

/**
 * Odds a new CPU game is "generous": the CPU never blocks and never completes
 * its own four, so the player wins as long as they go for it. Decided once
 * per game (see createCpuGameState) so the CPU isn't erratic within a game.
 */
export const CPU_GENEROUS_CHANCE = 0.53;

/** Prefer center columns when scores tie. */
const COLUMN_ORDER = [3, 2, 4, 1, 5, 0, 6];

function legalColumns(board) {
  const open = [];
  for (let c = 0; c < COLS; c++) {
    if (!isColumnFull(board, c)) open.push(c);
  }
  const ordered = COLUMN_ORDER.filter((c) => open.includes(c));
  const rest = open.filter((c) => !COLUMN_ORDER.includes(c));
  return [...ordered, ...rest];
}

function applyDrop(board, col, player) {
  const result = dropDisc(board, col, player);
  return result ? result.board : null;
}

function findWinningMove(board, player) {
  for (const col of legalColumns(board)) {
    const next = applyDrop(board, col, player);
    if (next && findWinningCells(next, player)) return col;
  }
  return -1;
}

function windowsForBoard(board) {
  const windows = [];

  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c <= COLS - 4; c++) {
      windows.push([board[r][c], board[r][c + 1], board[r][c + 2], board[r][c + 3]]);
    }
  }

  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r <= ROWS - 4; r++) {
      windows.push([board[r][c], board[r + 1][c], board[r + 2][c], board[r + 3][c]]);
    }
  }

  for (let r = 0; r <= ROWS - 4; r++) {
    for (let c = 0; c <= COLS - 4; c++) {
      windows.push([
        board[r][c],
        board[r + 1][c + 1],
        board[r + 2][c + 2],
        board[r + 3][c + 3],
      ]);
    }
  }

  for (let r = 3; r < ROWS; r++) {
    for (let c = 0; c <= COLS - 4; c++) {
      windows.push([
        board[r][c],
        board[r - 1][c + 1],
        board[r - 2][c + 2],
        board[r - 3][c + 3],
      ]);
    }
  }

  return windows;
}

function scoreWindow(cells, player, opponent) {
  let score = 0;
  let mine = 0;
  let theirs = 0;
  let empty = 0;

  for (const cell of cells) {
    if (cell === player) mine += 1;
    else if (cell === opponent) theirs += 1;
    else empty += 1;
  }

  if (mine > 0 && theirs > 0) return 0;
  if (mine === 4) return WIN_SCORE;
  if (theirs === 4) return -WIN_SCORE;
  if (mine === 3 && empty === 1) return 80;
  if (theirs === 3 && empty === 1) return -120;
  if (mine === 2 && empty === 2) return 12;
  if (theirs === 2 && empty === 2) return -16;
  if (mine === 1 && empty === 3) return 2;
  return 0;
}

function evaluateBoard(board, player, opponent) {
  if (findWinningCells(board, player)) return WIN_SCORE;
  if (findWinningCells(board, opponent)) return -WIN_SCORE;
  if (isBoardFull(board)) return 0;

  let total = 0;
  for (const window of windowsForBoard(board)) {
    total += scoreWindow(window, player, opponent);
  }
  return total;
}

function minimax(board, depth, maximizing, player, opponent, alpha, beta) {
  if (findWinningCells(board, player)) return WIN_SCORE - (MAX_DEPTH - depth);
  if (findWinningCells(board, opponent)) return -WIN_SCORE + (MAX_DEPTH - depth);
  if (depth === 0 || isBoardFull(board)) {
    return evaluateBoard(board, player, opponent);
  }

  const cols = legalColumns(board);

  if (maximizing) {
    let value = -Infinity;
    for (const col of cols) {
      const next = applyDrop(board, col, player);
      if (!next) continue;
      value = Math.max(
        value,
        minimax(next, depth - 1, false, player, opponent, alpha, beta)
      );
      alpha = Math.max(alpha, value);
      if (alpha >= beta) break;
    }
    return value === -Infinity ? evaluateBoard(board, player, opponent) : value;
  }

  let value = Infinity;
  for (const col of cols) {
    const next = applyDrop(board, col, opponent);
    if (!next) continue;
    value = Math.min(
      value,
      minimax(next, depth - 1, true, player, opponent, alpha, beta)
    );
    beta = Math.min(beta, value);
    if (alpha >= beta) break;
  }
  return value === Infinity ? evaluateBoard(board, player, opponent) : value;
}

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

/** Generous game: random centre-leaning move that neither wins nor blocks. */
function pickGenerousColumn(board, cpuPlayer, cols) {
  const nonWinning = cols.filter((col) => {
    const next = applyDrop(board, col, cpuPlayer);
    return next && !findWinningCells(next, cpuPlayer);
  });
  const pool = nonWinning.length ? nonWinning : cols;
  const weighted = pool.flatMap((col) =>
    Array(4 - Math.abs(3 - col)).fill(col)
  );
  return randomItem(weighted);
}

/** Beatable CPU: takes wins, usually blocks, sometimes errs, shallow minimax. */
export function pickCpuColumn(board, cpuPlayer, { generous = false } = {}) {
  const human = opponentPlayer(cpuPlayer);
  const cols = legalColumns(board);
  if (!cols.length) return -1;

  if (generous) return pickGenerousColumn(board, cpuPlayer, cols);

  const winNow = findWinningMove(board, cpuPlayer);
  if (winNow >= 0) return winNow;

  const blockNow = findWinningMove(board, human);
  if (blockNow >= 0 && Math.random() < BLOCK_CHANCE) return blockNow;

  if (Math.random() < MISTAKE_CHANCE) return randomItem(cols);

  const scored = [];
  for (const col of cols) {
    const next = applyDrop(board, col, cpuPlayer);
    if (!next) continue;
    const score = minimax(
      next,
      MAX_DEPTH - 1,
      false,
      cpuPlayer,
      human,
      -Infinity,
      Infinity
    );
    scored.push({ col, score });
  }
  if (!scored.length) return cols[0];

  const bestScore = Math.max(...scored.map((s) => s.score));
  const nearBest = scored.filter((s) => s.score >= bestScore - NEAR_BEST_MARGIN);
  return randomItem(nearBest).col;
}
